import React, { useState } from 'react';
import Breadcrumbs from '@grnhse/seedling/lib/birch/components/breadcrumbs';
import { SectionHeader } from '@grnhse/seedling/lib/birch/typography';
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@grnhse/seedling/lib/birch/components/tabs';
import { GlobalWhiteBackgroundImportant } from 'redesign_2022/global_white_background';
import { withLocalePrefix } from 'shared/utils/translation';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { type UserContextValue } from 'talent_matching/contexts/user_context';
import { BreakpointContextProvider } from 'talent_matching/contexts/breakpoint_context';
import { useDocumentTitle } from 'shared/utils/react_router_helper';
import { TalentMatchingFilters, RediscoveryProps } from 'talent_matching/types';
import TalentMatching from 'talent_matching/pages/talent_matching';
import TalentRediscovery from 'talent_matching/pages/talent_rediscovery';
import TalentMatchingQueryClientProvider from 'talent_matching/talent_matching_query_client';

const t = withLocalePrefix('talent_matching');

type Props = {
  initialFilters: Partial<TalentMatchingFilters>;
  jobTitle: string;
  previouslyCalibrated: boolean;
  pollRankingOnLoad: boolean;
  pollIndexingOnLoad: boolean;
  appReviewSessionId: number;
  showRediscoveryTab: boolean;
  rediscoveryProps?: RediscoveryProps;
} & UserContextValue;

const TalentMatchingPage = (props: Props) => {
  useDocumentTitle(t('document_title'));
  const { paths, jobTitle, showRediscoveryTab, rediscoveryProps } = props;
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const pageContent =
    showRediscoveryTab && rediscoveryProps ? (
      <Tabs
        index={activeTabIndex}
        onChange={setActiveTabIndex}
        data-provides="talent-matching-tabs"
      >
        <TabList>
          <Tab data-provides="talent-matching-app-review-tab">
            {t('tabs.app_review')}
          </Tab>
          <Tab data-provides="talent-matching-rediscovery-tab">
            {t('tabs.rediscovery')}
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel key="app-review">
            <TalentMatching {...props} isActive={activeTabIndex === 0} />
          </TabPanel>
          <TabPanel key="rediscovery">
            <TalentMatchingQueryClientProvider>
              <TalentRediscovery {...rediscoveryProps} />
            </TalentMatchingQueryClientProvider>
          </TabPanel>
        </TabPanels>
      </Tabs>
    ) : (
      <TalentMatching {...props} />
    );

  return (
    <BreakpointContextProvider>
      <GlobalWhiteBackgroundImportant />
      <Stack spacing={1}>
        <Breadcrumbs
          current={t('title')}
          crumbs={[
            {
              label: t('breadcrumbs.all_jobs'),
              href: paths.allJobsPath,
            },
            {
              label: jobTitle,
              href: paths.jobDashboardPath,
            },
          ]}
        />
        <SectionHeader large>{t('header')}</SectionHeader>
        {pageContent}
      </Stack>
    </BreakpointContextProvider>
  );
};

export default TalentMatchingPage;
